/**
 * Basic (non-Production) facilities: tblFacility.UseProduction = 0.
 *
 * WinForms gives them the older data model (docs/roadmap/basic-facilities.md): the
 * resident meal pattern is tblMeal (the Simple form), preferences are Likes/Dislikes,
 * the menu cycle is tblMenu_Detail (the Basic editor), and the Production-only screens
 * are not offered at all. These helpers say which web screens follow suit.
 */

interface ProductionFlag {
  useProduction?: boolean | null;
}

/**
 * True only when the facility settings have loaded and say UseProduction = false.
 * While settings are loading (null) the facility is treated as Production, as the
 * pages already default `useProduction ?? true`, so nothing flickers away.
 */
export function isBasicFacility(settings: ProductionFlag | null | undefined): boolean {
  return settings != null && settings.useProduction === false;
}

/**
 * Sidebar destinations WinForms hides for a Basic facility:
 * Global MP (frmDietech_Resident_DataGrid_V2.vb:1318-1342) and Selective Menu Touch
 * (frmMain.vb:1315, frmDietech_SelectiveMenu_Entry.vb:1587), and Forecasting, which the
 * desktop lists only under HasProductionFacility (V2.vb:6780-6793).
 */
export const PRODUCTION_ONLY_ROUTES: readonly string[] = ['/global-meal-patterns', '/selective-touch', '/forecasting'];

/**
 * Resident sections that are Production-only. Adjustments live in the Production meal
 * pattern form, which a Basic resident never opens (ucDietech_Resident_DataGrid.vb:1298);
 * the Selective Menu group is hidden for Basic (V2:1318-1342).
 */
export const PRODUCTION_ONLY_RESIDENT_SECTIONS: readonly string[] = ['adjustments', 'selective-menu'];

/** Whether a sidebar route is offered for this facility. */
export function routeAllowedForFacility(route: string | undefined, settings: ProductionFlag | null | undefined): boolean {
  if (!route) return true;
  return !(isBasicFacility(settings) && PRODUCTION_ONLY_ROUTES.includes(route));
}

/** Whether a resident section is offered for this facility. */
export function residentSectionAllowed(sectionKey: string, settings: ProductionFlag | null | undefined): boolean {
  return !(isBasicFacility(settings) && PRODUCTION_ONLY_RESIDENT_SECTIONS.includes(sectionKey));
}
